import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUpIcon } from "@phosphor-icons/react/dist/csr/CaretUp";
import { FileMagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/FileMagnifyingGlass";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useRecordSource } from "@/profitability/sources/use-record-source";
import { runDates } from "@/profitability/utils";
import type { RecordObject, RecordRef } from "./objects";
import { sameRecord, unitOf } from "./objects";
import { RecordHeader } from "./record-header";
import type { RecordPeriod } from "./record-view/record-missing";
import { RecordSections } from "./record-view/record-sections";
import { useRecord } from "./record-view/use-record";

/** The records a panel was opened from, to step through them in order. */
export interface RecordList {
  label: string;
  refs: RecordRef[];
  /** The list's opener, when the list itself closes before this panel opens. */
  returnFocus?: HTMLElement | null;
}

/**
 * A record's preview in the page's right sidebar: step to the previous or
 * next record of the list it was opened from.
 */
export const RecordPanel = ({
  businessId,
  reportId,
  objects,
  selected,
  list,
  onOpenRecord,
  onClose,
  period = null,
  canUpload = false,
  onAsk,
  showFlag = true,
}: {
  businessId: string;
  reportId: string;
  objects: RecordObject[];
  selected: RecordRef;
  list?: RecordList | null;
  onOpenRecord: (ref: RecordRef, list?: RecordList | null) => void;
  onClose: () => void;
  /** The period on the page, and its run: where a missing figure is filled. */
  period?: RecordPeriod | null;
  /** Whether the person may upload files for a period (`canManageReport`). */
  canUpload?: boolean;
  /** Asks Volca in the page's chat. */
  onAsk?: (question: string) => void;
  /** The finding's own banner and timeline. Off when the page already shows them. */
  showFlag?: boolean;
}) => {
  const data = useRecord(businessId, reportId, objects, selected, period);
  const index = list
    ? list.refs.findIndex((ref) => sameRecord(ref, selected))
    : -1;
  const step = (by: number) => {
    const next = list?.refs[index + by];
    if (next) onOpenRecord(next, list);
  };
  // A linked record opened from here is outside the list it came from.
  const open = (ref: RecordRef) => onOpenRecord(ref, null);
  const { object, row, week } = data;
  // A row from an earlier period is filled, and was read, in that period.
  const shownPeriod = week
    ? { dates: runDates(week), runId: data.weekRunId }
    : period;
  const showSource = useRecordSource(
    object,
    row,
    week ? data.weekRunId : period?.runId,
    shownPeriod?.dates,
  );

  return (
    <Stack
      gap={0}
      align="stretch"
      className="min-h-0 min-w-0 flex-1 pointer-coarse:[&_[data-slot=button]]:min-h-11 pointer-coarse:[&_[data-slot=button]]:min-w-11 pointer-coarse:[&_button]:min-h-11 pointer-coarse:[&_button]:min-w-11"
    >
      <Stack
        direction="row"
        align="center"
        gap={2}
        className="min-h-12 shrink-0 flex-wrap px-5 py-1"
      >
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Close"
          onClick={onClose}
        >
          <XIcon aria-hidden />
        </Button>
        {showSource && (
          <Button variant="ghost" size="sm" onClick={() => showSource()}>
            <FileMagnifyingGlassIcon aria-hidden data-icon="inline-start" />
            Show sources
          </Button>
        )}
        {index >= 0 && list && (
          <span className="ml-auto flex items-center gap-2">
            <Text
              variant="caption"
              tone="muted"
              className="whitespace-nowrap tabular-nums"
              aria-live="polite"
            >
              {index + 1} of {list.refs.length}
              <span className="sr-only"> in {list.label}</span>
            </Text>
            <span className="flex items-center rounded-lg border border-line bg-surface-raised p-0.5">
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Previous record"
                disabled={index === 0}
                onClick={() => step(-1)}
              >
                <CaretUpIcon aria-hidden />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Next record"
                disabled={index === list.refs.length - 1}
                onClick={() => step(1)}
              >
                <CaretDownIcon aria-hidden />
              </Button>
            </span>
          </span>
        )}
      </Stack>
      {object && row ? (
        <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <RecordHeader
            object={object}
            objects={objects}
            row={row}
            name={data.name}
            unit={unitOf(object, objects, row)}
            finding={data.finding}
            week={week}
            onOpenRecord={open}
          />
          <RecordSections
            businessId={businessId}
            reportId={reportId}
            data={{ ...data, object, row }}
            objects={objects}
            onOpenRecord={open}
            period={shownPeriod}
            onShowSource={showSource}
            canUpload={canUpload}
            onAsk={onAsk}
            showFlag={showFlag}
          />
        </div>
      ) : data.loading ? (
        <Stack gap={6} className="p-5" aria-busy role="status">
          <span className="sr-only">Loading record</span>
          <Skeleton className="h-8 w-48" aria-hidden />
          <Skeleton className="h-24 w-full" aria-hidden />
          <Skeleton className="h-32 w-full" aria-hidden />
          <Skeleton className="h-24 w-full" aria-hidden />
        </Stack>
      ) : (
        <Text tone="muted" className="p-5">
          This record is not in the latest report.
        </Text>
      )}
    </Stack>
  );
};
